import { useMemo, VFC } from 'react';

import FieldSchemaSelect from 'dashboard/components/FieldSchemaSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { snakify, UsableObjectConfig } from 'dashboard/lib/workflows';
import { WorkflowError, WorkflowFieldKey, WorkflowFieldObjectKey } from 'dashboard/models/workflow';
import { WorkflowActionClearbitBusinessLookupV1 } from 'dashboard/models/workflow-version';
import Spacer from 'design-system/components/Spacer';

interface ParamsClearbitBusinessLookupV1Props {
  editable: boolean;
  params: WorkflowActionClearbitBusinessLookupV1['params'];
  errors: WorkflowError[];
  usableObjects: UsableObjectConfig[];
  updateActionParams: (newParams: WorkflowActionClearbitBusinessLookupV1['params']) => void;
}

const ParamsClearbitBusinessLookupV1: VFC<ParamsClearbitBusinessLookupV1Props> = ({
  editable,
  params,
  errors,
  usableObjects,
  updateActionParams,
}) => {
  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionClearbitBusinessLookupV1['params']>(errors);

  const allowedFieldPaths = useMemo(() => {
    return usableObjects.map((usableObject) => {
      const { value, workflowObjectKey } = usableObject;
      const fieldPath =
        workflowObjectKey === WorkflowFieldObjectKey.Inquiry
          ? `${value}.${WorkflowFieldKey.Fields}`
          : value;
      return snakify(fieldPath);
    });
  }, [usableObjects]);

  const onSelectEmailAddress = (
    emailAddress: WorkflowActionClearbitBusinessLookupV1['params']['input-fields']['email-address'],
  ) => {
    updateActionParams({
      ...params,
      'input-fields': {
        ...params['input-fields'],
        'email-address': emailAddress,
      },
    });
  };

  return (
    <>
      <Spacer height={2} />
      <ParamLabel
        labelText='Email address'
        htmlFor='params-clearbit-business-lookup__email-input'
      />
      <FieldSchemaSelect
        id='params-clearbit-business-lookup__email'
        initialValue={params['input-fields']['email-address'] || ''}
        usableObjects={usableObjects}
        allowedFieldPaths={allowedFieldPaths}
        onFieldSelect={(option) => onSelectEmailAddress(option.value)}
        disabled={!editable}
        size='md'
        hasError={hasErrorFor('input-fields')}
      />
      {renderErrorMessageFor('input-fields')}
    </>
  );
};

export default ParamsClearbitBusinessLookupV1;
